import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import DashboardTransactionsEmpty from '@/features/dashboard/components/DashboardTransactions/DashboardTransactionsEmpty';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import { cn } from '@/lib/utils';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useDashboardRecentTransactionsApi } from '@/features/dashboard/hooks/useDashboardRecentTransactionsApi';
import { TransactionsUtils } from '@/features/transactions/utils/transactionsUtils';
import TransactionDetails from '@/features/transactions/components/TransactionDetails';

type Props = {
    isMobile?: boolean;
};

const DashboardTransactions: FC<Props> = ({ isMobile }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [transactionId, setTransactionId] = useState('');

    const navigate = useNavigate();
    const { t } = useTranslation();

    const { recentTransactionsData, isLoadingRecentTransactionsData } =
        useDashboardRecentTransactionsApi();

    const handleShowTransactionDetails = (id: string) => {
        setTransactionId(id);
        setShowDetails(true);
    };

    return (
        <div className="grow flex flex-col gap-4">
            <div className="flex-1">
                {isLoadingRecentTransactionsData && <EActivitiesListSkeleton isMobile={isMobile} />}
                {!isLoadingRecentTransactionsData && !!recentTransactionsData?.groups?.length && (
                    <div className={cn('flex flex-col gap-6', isMobile && 'gap-4')}>
                        {recentTransactionsData?.groups.map(({ date, transactions }) => {
                            return (
                                <div key={`${date}`}>
                                    <h5 className="text-sm text-popover-800">
                                        {DateTimeHelper.formatDate(new Date(date), 2)}
                                    </h5>
                                    {transactions?.map((transaction) => {
                                        const { icon, colorClass } =
                                            TransactionsUtils.getTypeConfig(transaction.type);

                                        const { label: feeLabel } = TransactionsUtils.getFeeConfig(
                                            transaction.sign,
                                        );

                                        const descriptionText = `${
                                            transaction.apartmentName
                                        }\u00A0\u00A0\u00A0·\u00A0\u00A0\u00A0${t(`${feeLabel ?? ''}`)}`;

                                        return (
                                            <EActivitiesElement
                                                key={transaction.id}
                                                icon={icon}
                                                colorClass={colorClass}
                                                header={
                                                    transaction.serviceName ??
                                                    t(
                                                        'main.dashboard.components.recentActivities.list.transactions.noServices',
                                                    )
                                                }
                                                description={descriptionText}
                                                value={transaction.amount}
                                                id={transaction.id}
                                                onClick={handleShowTransactionDetails}
                                                isMobile={isMobile}
                                                asset={transaction.type}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!isLoadingRecentTransactionsData && !recentTransactionsData?.groups?.length && (
                    <DashboardTransactionsEmpty />
                )}
            </div>
            <Button
                variant={'outline'}
                className="w-full text-sm font-medium"
                onClick={() => navigate('/finances/transactions')}
            >
                {t('main.dashboard.components.recentActivities.list.transactions.seeMoreButton')}
            </Button>
            {showDetails && (
                <TransactionDetails
                    id={transactionId}
                    onClose={() => setShowDetails(false)}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export default DashboardTransactions;
